import { useMutation, useQuery, useQueryClient, type UseQueryOptions } from "@tanstack/react-query";
import { api, ApiError, qs } from "./api";
import { useToast } from "@/components/Toast";
import type {
  AppConfigDTO,
  ApiTokenDTO,
  ApprovalsDTO,
  BankDTO,
  BankEntryDTO,
  GateDTO,
  GoalDTO,
  IntegrationDTO,
  JobDTO,
  JobRunDTO,
  KidJobDTO,
  KidResponsibilityDTO,
  KidSummaryDTO,
  LedgerEntryDTO,
  MeDTO,
  NotificationDTO,
  PenaltyPresetDTO,
  RedemptionDTO,
  ResponsibilityDTO,
  RewardTypeDTO,
  StoreItemDTO,
} from "@shared/types";

type Opts<T> = Omit<UseQueryOptions<T, ApiError>, "queryKey" | "queryFn">;

/** Public app settings (e.g. the marketing site's URL); they only change on redeploy. */
export function useAppConfig() {
  return useQuery<AppConfigDTO, ApiError>({
    queryKey: ["config"],
    queryFn: () => api.get<AppConfigDTO>("/config"),
    staleTime: Infinity,
  });
}

export function useMe(opts?: Opts<MeDTO | null>) {
  return useQuery<MeDTO | null, ApiError>({
    queryKey: ["me"],
    queryFn: async () => {
      try {
        return await api.get<MeDTO>("/auth/me");
      } catch (err) {
        if (err instanceof ApiError && err.status === 401) return null;
        throw err;
      }
    },
    staleTime: 30_000,
    ...opts,
  });
}

export const useKids = () =>
  useQuery<{ kids: KidSummaryDTO[] }, ApiError>({
    queryKey: ["kids"],
    queryFn: () => api.get("/kids"),
    refetchInterval: 30_000,
  });

export const useKidSummary = (kidId: string | undefined) =>
  useQuery<KidSummaryDTO, ApiError>({
    queryKey: ["kid", kidId],
    queryFn: () => api.get(`/kids/${kidId}/summary`),
    enabled: !!kidId,
    refetchInterval: 30_000,
  });

export const useLedger = (kidId: string | undefined, limit = 50) =>
  useQuery<{ entries: LedgerEntryDTO[] }, ApiError>({
    queryKey: ["ledger", kidId, limit],
    queryFn: () => api.get(`/kids/${kidId}/ledger${qs({ limit })}`),
    enabled: !!kidId,
  });

export const useBankEntries = (kidId: string | undefined) =>
  useQuery<{ entries: BankEntryDTO[] }, ApiError>({
    queryKey: ["bank-entries", kidId],
    queryFn: () => api.get(`/kids/${kidId}/bank`),
    enabled: !!kidId,
  });

export const useKidResponsibilities = () =>
  useQuery<{ items: KidResponsibilityDTO[]; gate: GateDTO }, ApiError>({
    queryKey: ["responsibilities", "kid"],
    queryFn: () => api.get("/responsibilities"),
    refetchInterval: 30_000,
  });

export const useParentResponsibilities = () =>
  useQuery<
    { responsibilities: ResponsibilityDTO[]; board: Record<string, { items: KidResponsibilityDTO[]; gate: GateDTO }> },
    ApiError
  >({
    queryKey: ["responsibilities", "parent"],
    queryFn: () => api.get("/responsibilities"),
    refetchInterval: 30_000,
  });

/** A kid's jobs with lock state: their own (kids), or one kid's as a parent sees them (kidId). */
export const useKidJobs = (kidId?: string) =>
  useQuery<{ jobs: KidJobDTO[]; gate: GateDTO }, ApiError>({
    queryKey: ["jobs", "kid", kidId ?? "me"],
    queryFn: () => api.get(`/jobs${qs({ kidId })}`),
    refetchInterval: 30_000,
  });

export const useParentJobs = () =>
  useQuery<{ jobs: JobDTO[] }, ApiError>({ queryKey: ["jobs", "parent"], queryFn: () => api.get("/jobs") });

export const useMyPitches = () =>
  useQuery<{ jobs: JobDTO[] }, ApiError>({ queryKey: ["pitches"], queryFn: () => api.get("/jobs/pitches") });

export const useJobRuns = (params: { kidId?: string; status?: string } = {}) =>
  useQuery<{ runs: JobRunDTO[] }, ApiError>({
    queryKey: ["job-runs", params],
    queryFn: () => api.get(`/job-runs${qs(params)}`),
  });

export const useApprovals = (enabled = true) =>
  useQuery<ApprovalsDTO, ApiError>({
    queryKey: ["approvals"],
    queryFn: () => api.get("/approvals"),
    refetchInterval: 20_000,
    enabled,
  });

export const useRewardTypes = () =>
  useQuery<{ rewardTypes: RewardTypeDTO[] }, ApiError>({
    queryKey: ["rewards"],
    queryFn: () => api.get("/rewards"),
    staleTime: 60_000,
  });

export const useBanks = (kidId?: string) =>
  useQuery<{ banks: BankDTO[] }, ApiError>({
    queryKey: ["banks", kidId ?? "me"],
    queryFn: () => api.get(`/rewards/banks${qs({ kidId })}`),
    // Keeps a screen-time countdown in step with the server (a parent may end it, or another device).
    refetchInterval: 30_000,
  });

export const useStore = () =>
  useQuery<{ items: StoreItemDTO[] }, ApiError>({ queryKey: ["store"], queryFn: () => api.get("/store") });

export const useGoals = (kidId?: string) =>
  useQuery<{ goals: GoalDTO[] }, ApiError>({
    queryKey: ["goals", kidId ?? "me"],
    queryFn: () => api.get(`/goals${qs({ kidId })}`),
  });

export const useRedemptions = (params: { kidId?: string; status?: string } = {}) =>
  useQuery<{ redemptions: RedemptionDTO[] }, ApiError>({
    queryKey: ["redemptions", params],
    queryFn: () => api.get(`/redemptions${qs(params)}`),
  });

export const usePenalties = () =>
  useQuery<{ presets: PenaltyPresetDTO[] }, ApiError>({ queryKey: ["penalties"], queryFn: () => api.get("/penalties") });

export const useActivity = () =>
  useQuery<{ entries: LedgerEntryDTO[] }, ApiError>({
    queryKey: ["activity"],
    queryFn: () => api.get("/activity"),
    refetchInterval: 30_000,
  });

export const useNotifications = () =>
  useQuery<{ notifications: NotificationDTO[]; unread: number }, ApiError>({
    queryKey: ["notifications"],
    queryFn: () => api.get("/notifications"),
    refetchInterval: 30_000,
  });

export const useIntegrations = () =>
  useQuery<{ integrations: IntegrationDTO[]; tokens: ApiTokenDTO[]; mcpUrl: string }, ApiError>({
    queryKey: ["integrations"],
    queryFn: () => api.get("/integrations"),
  });

export const useGrants = () =>
  useQuery<{ grants: BankEntryDTO[] }, ApiError>({ queryKey: ["grants"], queryFn: () => api.get("/integrations/grants") });

/**
 * A mutation that shows errors as toasts and refreshes all data afterwards. The app's data
 * is small, so refreshing everything keeps every screen consistent across family members.
 */
export function useAction<TVars = void, TResult = unknown>(
  fn: (vars: TVars) => Promise<TResult>,
  opts: { success?: string | ((result: TResult) => string | null); onSuccess?: (result: TResult, vars: TVars) => void } = {},
) {
  const qc = useQueryClient();
  const toast = useToast();
  return useMutation<TResult, ApiError, TVars>({
    mutationFn: fn,
    onSuccess: async (result, vars) => {
      const message = typeof opts.success === "function" ? opts.success(result) : opts.success;
      if (message) toast.show(message, "success");
      opts.onSuccess?.(result, vars);
      await qc.invalidateQueries();
    },
    onError: (err) => toast.show(err.message, "error"),
  });
}
